import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import parse from 'html-react-parser';

import { Tooltip } from "../Tooltip/Tooltip";

import {
  _ThumbnailsList as ThumbnailsList,
  _VerticalList as VerticalList,
  _CardsList as CardsList,
  _IconContainer as IconContainer,
  _OptionListContainer as OptionListContainer,
} from "./style";
import SvgComponent from "../SvgComponent/SvgComponent";
import Dropdown from "./Dropdown";
import FormLabel from "@mui/material/FormLabel";
import Box from "@mui/material/Box";

const OptionList = ({
  renderAs,
  name,
  label,
  tooltipContent = "",
  description = "",
  options = [],
  activeOptionValueFromParent = null,
  activeImage = null,
  onChange,
  desktopRenderWidth = "auoto",
  background,
  hideBorder,
  largePadding,
}) => {
  const [activeValue, setActiveValue] = useState(activeOptionValueFromParent);

  useEffect(() => {
    setActiveValue(activeOptionValueFromParent);
  }, [activeOptionValueFromParent]);

  const handleChange = (value) => {
    setActiveValue(value);

    onChange(value);
  };

  const ActiveDescription = () => {
    const activeOption = options.find((option) => option.value === activeValue);

    return activeOption && activeOption.description ? (
      <p className="description">{activeOption.description}</p>
    ) : null;
  };

  // Select can also be present but disabled when there is nothing to select
  if (renderAs === "select" || renderAs === "disabled") {
    return (
      <Dropdown
        label={label}
        values={options}
        defaultValue={activeValue}
        onChange={handleChange}
        tooltipContent={tooltipContent}
        disabled={renderAs === 'disabled'}
        image={activeImage}
      />
    );
  }

  if (renderAs === "thumbnails") {
    return (
      <OptionListContainer
        hideBorder={hideBorder}
        background={background}
        largePadding={largePadding}
      >
        <ThumbnailsList desktopRenderWidth={desktopRenderWidth}>
          <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
            <FormLabel
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                display: "inlineBlock",
                fontFamily: "aktiv-grotesk, sans-serif",
                marginBottom: "4px",
                color: "#000000",
                "&.Mui-focused": {
                  color: "#111111",
                },
              }}
            >
              {label}
              {tooltipContent && (
                <Tooltip
                  content={tooltipContent}
                  dataId={`Thumbnails_${name}List_Tooltip`}
                />
              )}
            </FormLabel>     

            {description && (
              <p className="description">{parse(description)}</p>
            )}
          </Box>

          <div className="options">
            {options.map((option) => (
              <label key={`${name}__${option.value}`}>
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={option.value === activeValue}
                  onChange={(event) => {
                    handleChange(event.target.value);
                  }}
                />
                {option.imgSrc && (
                  <img
                    src={option.imgSrc}
                    alt={option.imgAlt || option.label || ""}
                  />
                )}
                {option.label}
              </label>
            ))}
          </div>
        </ThumbnailsList>
      </OptionListContainer>
    );
  }

  if (renderAs === "verticalList") {
    return (
      <OptionListContainer hideBorder={hideBorder} background={background} $noPaddingTop={!label}>
        <VerticalList>
          <h2 className="heading" data-id={`VerticalList${name}List__heading`}>
            {label}
            {tooltipContent && (
              <Tooltip
                content={tooltipContent}
                dataId={`VerticalList${name}List__Tooltip`}
              />
            )}
          </h2>

          {description && (
            <p className="description">{parse(description)}</p>
          )}
          <div>
            {options.map((option) => (
              <label key={`${name}__${option.value}`}>
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={option.value === activeOptionValueFromParent}
                  onChange={(event) => {
                    handleChange(event.target.value);
                  }}
                />
                <div className="options">
                  {option.imgSrc && (
                    <img
                      src={option.imgSrc}
                      alt={option.imgAlt || option.label || ""}
                    />
                  )}
                  {option.label}
                  {option.value === activeOptionValueFromParent && (
                    <IconContainer>
                      <SvgComponent
                        name="checkmark"
                        height="16px"
                        width="16px"
                      />
                    </IconContainer>
                  )}
                </div>
              </label>
            ))}
          </div>
        </VerticalList>
      </OptionListContainer>
    );
  }

  // TODO, thumbnails and cards are pretty much the same --> refactor this
  if (renderAs === "cards") {
    return (
      <OptionListContainer hideBorder={hideBorder} background={background}>
        <CardsList>
          <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
            <FormLabel
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                display: "inlineBlock",
                fontFamily: "aktiv-grotesk, sans-serif",
                marginBottom: "4px",
                color: "#000000",
                "&.Mui-focused": {
                  color: "#111111",
                },
              }}
            >
              {label}
              {tooltipContent && (
                <Tooltip
                  content={tooltipContent}
                  dataId={`Thumbnails_${name}List_Tooltip`}
                />
              )}
            </FormLabel>
            
            {description && (
              <p className="description">{parse(description)}</p>
            )}
          </Box>

          <div className="options">
            {options.map((option) => (
              <React.Fragment key={`${name}__${option.value}`}>
                <input
                  id={option.value}
                  type="radio"
                  name={option.value}
                  value={option.value}
                  checked={option.value === activeValue}
                  onChange={(event) => {
                    handleChange(event.target.value);
                  }}
                  disabled={option.disabled}
                />
                <label key={option.imgSrc} htmlFor={option.value} style={{ filter: option.disabled ? "grayscale()" : "none" }}>
                  <SvgComponent name={option.iconName} />
                  <h5>{option.label}</h5>
                </label>
              </React.Fragment>
            ))}
          </div>
          <ActiveDescription />
        </CardsList>
      </OptionListContainer>
    );
  }

  return null;
};

OptionList.propTypes = {
  renderAs: PropTypes.oneOf([
    "select",
    "radio",
    "disabled",
    "thumbnails",
    "cards",
    "verticalList",
  ]).isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  tooltipContent: PropTypes.node,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
      cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      imgSrc: PropTypes.string,
      imgAlt: PropTypes.string,
    })
  ),
  disabledMessage: PropTypes.string,
  desktopRenderWidth: PropTypes.string,
  background: PropTypes.string,
  hideBorder: PropTypes.bool,
  activeOptionValueFromParent: PropTypes.string,
  activeImage: PropTypes.shape({
    imgSrc: PropTypes.string,
    imgAlt: PropTypes.string,
  }),
  onChange: PropTypes.func,
  option: PropTypes.object,
  largePadding: PropTypes.bool,
};

export default OptionList;
